import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker';
import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfjs, Document, Page } from 'react-pdf';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
} from '@heroui/react';

pdfjs.GlobalWorkerOptions.workerPort = new pdfWorker();

export function PdfPreview({ file }: { file: string }) {
  const { t } = useTranslation('inbox');

  const [hasPreview, setHasPreview] = useState<boolean>(false);
  const [modal, toggleModal] = useState<boolean>(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);

  useEffect(() => {
    if (!file) return;

    const checkPdf = async (url: string) => {
      try {
        const response = await fetch(url);
        if (!response.ok) return;

        const blob = await response.blob();
        const text = await blob.text();

        const isEncrypted = text.includes('/Encrypt');
        const metadataBlock = text.substring(
          text.lastIndexOf('<<'),
          text.lastIndexOf('>>')
        );
        const isEncryptedInMetadata = metadataBlock.includes('/Encrypt');

        if (!isEncrypted && !isEncryptedInMetadata) {
          setHasPreview(true);
        }
      } catch (e) {
        console.error(e);
      }
    };

    checkPdf(file);
  }, [file]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };

  const changePage = (offset: number) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  };

  return (
    <>
      <Modal
        isOpen={modal}
        onClose={() => {
          toggleModal(false);
          setPageNumber(1);
        }}
        scrollBehavior="inside"
        size="5xl"
      >
        <ModalContent>
          <ModalBody>
            <div className="h-[800px] pt-12 m-auto">
              <p className="text-center mb-4">
                {t('pdfpreview.modal.page_count', {
                  page: pageNumber || (numPages ? 1 : '--'),
                  totalPage: numPages || '--',
                })}
              </p>
              <Document file={file} onLoadSuccess={onDocumentLoadSuccess}>
                <Page
                  pageNumber={pageNumber}
                  height={800}
                  width={800}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </Document>
            </div>
          </ModalBody>
          <ModalFooter className="flex justify-between">
            <div>
              <Button
                aria-label={t('common.action.back')}
                color="primary"
                className="mr-0.5"
                isIconOnly
                isDisabled={pageNumber <= 1}
                onPress={() => changePage(-1)}
                variant="faded"
              >
                <Icon icon="mdi:navigate-before" className="text-2xl" />
              </Button>
              <Button
                aria-label={t('common.action.next')}
                color="primary"
                isIconOnly
                isDisabled={pageNumber >= numPages}
                onPress={() => changePage(1)}
                variant="faded"
              >
                <Icon icon="mdi:navigate-next" className="text-2xl" />
              </Button>
            </div>
            <div>
              <Button
                aria-label={t('common.action.download')}
                color="primary"
                isIconOnly
                onPress={() => window.open(file, '_blank')}
                variant="faded"
              >
                <Icon icon="mdi:download" className="text-2xl" />
              </Button>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Card
        className="w-[325px] h-[350px]"
        isPressable={hasPreview}
        onPress={() => toggleModal(true)}
        radius="sm"
        shadow="sm"
      >
        <CardHeader className="flex flex-col items-end py-0 pt-1 pr-1">
          <Button
            aria-label={t('common.action.download')}
            color="primary"
            isIconOnly
            onPress={() => window.open(file, '_blank')}
            size="sm"
            variant="faded"
          >
            <Icon icon="mdi:download" className="text-lg" />
          </Button>
        </CardHeader>
        <CardBody className="pt-1">
          {hasPreview ? (
            <Document file={file} onLoadSuccess={onDocumentLoadSuccess}>
              <Page
                pageNumber={1}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                scale={0.5}
              />
            </Document>
          ) : (
            <div className="m-auto">
              <Icon
                icon="ri:lock-password-fill"
                className="text-9xl text-gray-400"
              />
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}

interface PdfAttachmentProps {
  file: File;
  setFile: (file: File | null) => void;
}

export function PdfAttachment({ file, setFile }: PdfAttachmentProps) {
  const { t } = useTranslation('inbox');

  return (
    <Card className="inline-flex" radius="sm" shadow="sm">
      <CardBody>
        <span className="flex place-items-center">
          <Icon icon="bi:file-pdf-fill" className="text-2xl text-primary-500" />
          <p className="text-sm ml-0.5 mr-2">{file.name}</p>
          <Button
            aria-label={t('common.action.clear')}
            isIconOnly
            onPress={() => setFile(null)}
            size="sm"
            variant="light"
            radius="full"
          >
            <Icon icon="tabler:x" className="text-lg" />
          </Button>
        </span>
      </CardBody>
    </Card>
  );
}
